import clsx from 'clsx';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Bot, Info, Minus, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import type { Period } from '../lib/period';
import type { AiView, MoneyLine, Notice, ScoreLabel } from '../lib/types';
import { Badge, Card, CardHeader, type BadgeTone } from './ui';

/**
 * The Executive Dashboard's own pieces, beside the shared kit (ui.tsx): money that is never shown as zero
 * when it is unknown, changes only between two known values, Finance's trust status, the period's status
 * and as-of time, "not enough evidence", and AI's explanations — always labelled, always optional.
 */

const LOCALE_TAG = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' } as const;

/** A number in the reader's locale (Western digits in Arabic too), or a dash. */
export function useNumber() {
  const { locale } = useI18n();
  return (value: number | null | undefined, digits = 0) =>
    value === null || value === undefined || !Number.isFinite(value)
      ? '—'
      : new Intl.NumberFormat(LOCALE_TAG[locale], { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value);
}

/** An amount in a currency — or `null` → "Unknown", never 0. */
export function useMoney() {
  const { locale, t } = useI18n();
  return (value: number | null | undefined, currency: string) => {
    if (value === null || value === undefined || !Number.isFinite(value)) return t('common.unknown');
    const text = new Intl.NumberFormat(LOCALE_TAG[locale], { style: 'currency', currency: currency || 'EGP', maximumFractionDigits: 0 }).format(value);
    // A Latin currency symbol stays whole right to left ("US$", not "$US").
    return locale === 'ar' ? `⁦${text}⁩` : text;
  };
}

/** Finance's figure: the base amount, or why there is none; its unconverted currencies and today's-rate parts said. */
export function MoneyValue({ line, compact = false }: { line: MoneyLine | null | undefined; compact?: boolean }) {
  const { t } = useI18n();
  const money = useMoney();
  if (!line) return <span className="text-muted">{t('common.unknown')}</span>;
  return (
    <span className="inline-flex flex-col">
      <span className={clsx('tabular', !line.complete && 'text-warning-fg dark:text-warning-dark')}>
        {line.complete ? money(line.base, line.currency) : t('state.partial')}
      </span>
      {!compact && !line.complete && line.unconverted.length > 0 && (
        <span className="text-caption text-muted">{t('state.unconverted', { currencies: line.unconverted.join(', ') })}</span>
      )}
      {!compact && line.atTodayRate && line.complete && <span className="text-caption text-muted">{t('state.atTodayRate')}</span>}
    </span>
  );
}

/** A change between two known values; nothing at all when either is unknown. `goodWhenDown` for costs and overdue work. */
export function Delta({
  current,
  previous,
  basis,
  goodWhenDown = false,
  format,
}: {
  current: number | null | undefined;
  previous: number | null | undefined;
  basis?: string;
  goodWhenDown?: boolean;
  format?: (n: number) => string;
}) {
  const { t, has } = useI18n();
  const num = useNumber();
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return <span className="text-caption text-muted">{t('state.noComparison')}</span>;
  }
  const diff = current - previous;
  const pct = previous !== 0 ? Math.round((diff / Math.abs(previous)) * 1000) / 10 : null;
  const up = diff > 0;
  const flat = diff === 0;
  const good = flat ? null : goodWhenDown ? !up : up;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const basisText = basis && has(`period.basis.${basis}`) ? t(`period.basis.${basis}` as TranslationKey) : (basis ?? '');
  const value = pct !== null ? `${num(Math.abs(pct), 1)}%` : format ? format(Math.abs(diff)) : num(Math.abs(diff), 2);
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 text-caption tabular',
        good === null ? 'text-muted' : good ? 'text-success-fg dark:text-success' : 'text-error-fg dark:text-error-dark',
      )}
    >
      <Icon className="h-3.5 w-3.5 flip-rtl" aria-hidden />
      {t('state.change', { sign: flat ? '' : up ? '+' : '−', value, basis: basisText })}
    </span>
  );
}

const TRUST_TONE: Record<string, BadgeTone> = {
  VERIFIED: 'success',
  PREVIEW: 'info',
  UNVERIFIED: 'warning',
  PARTIAL: 'warning',
  NOT_RECONCILED: 'error',
  SETUP_NEEDED: 'warning',
  UNAVAILABLE: 'neutral',
  RESTRICTED: 'neutral',
};

/** Finance's own trust status of a figure or report. */
export function TrustBadge({ status }: { status: string | null | undefined }) {
  const { t, has } = useI18n();
  if (!status) return null;
  const key = `trust.${status}`;
  return <Badge tone={TRUST_TONE[status] ?? 'neutral'}>{has(key) ? t(key) : status}</Badge>;
}

const STATUS_TONE: Record<string, BadgeTone> = { LIVE: 'info', PROVISIONAL: 'warning', FINALIZED: 'success', RECALCULATED: 'plasma' };

/** Live, provisional, finalized or recalculated. */
export function SnapshotStatus({ status }: { status: string }) {
  const { t, has } = useI18n();
  const key = `status.${status}`;
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{has(key) ? t(key) : status}</Badge>;
}

/** The period read: its range, the company's time zone, live or not, what it is compared with, and as of when. */
export function PeriodLine({ period, asOf }: { period: Period | null | undefined; asOf?: string | null }) {
  const { t, locale, has } = useI18n();
  if (!period) return null;
  const range = period.days === 1 ? period.start : `${period.start} → ${period.last}`;
  const basisKey = `period.basis.${period.comparison.basis}`;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-muted">
      <span className="tabular" dir="ltr">
        {range}
      </span>
      <SnapshotStatus status={period.live ? 'LIVE' : 'PROVISIONAL'} />
      <span>{t('period.timezone', { tz: period.timezone })}</span>
      <span>{t('period.compareWith', { range: has(basisKey) ? t(basisKey) : period.comparison.basis })}</span>
      {asOf && <span>{t('period.asOf', { time: formatDateTime(asOf, locale) })}</span>}
    </div>
  );
}

/** "Not enough evidence" / "Not measurable", with the reason in words when there is one. */
export function NotMeasurable({ reason, compact = false }: { reason?: string | null; compact?: boolean }) {
  const { t } = useI18n();
  const text = reason === 'INSUFFICIENT_COVERAGE' ? t('state.notMeasurable') : t('state.notEnoughEvidence');
  return (
    <span className={clsx('inline-flex items-center gap-1 text-muted', compact ? 'text-caption' : 'text-body-sm')}>
      <Info className="h-3.5 w-3.5" aria-hidden />
      {text}
    </span>
  );
}

const LABEL_TONE: Record<ScoreLabel, BadgeTone> = { EXCELLENT: 'success', VERY_GOOD: 'info', GOOD: 'neutral', NEEDS_ATTENTION: 'warning' };

/** A work score out of 100 with its label — or "Not enough evidence". The label never replaces the number. */
export function ScoreBadge({ score, label, reason, labelText }: { score: number | null; label: ScoreLabel | null; reason?: string | null; labelText?: string }) {
  const num = useNumber();
  if (score === null) return <NotMeasurable reason={reason} compact />;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-title tabular">{num(score, 0)}</span>
      {label && <Badge tone={LABEL_TONE[label]}>{labelText ?? label}</Badge>}
    </span>
  );
}

/** A top-line figure, linking to the page that explains it. */
export function KpiCard({
  label,
  value,
  hint,
  delta,
  to,
  tone = 'neutral',
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  delta?: ReactNode;
  to?: string;
  tone?: 'neutral' | 'warning' | 'error' | 'success';
}) {
  const body = (
    <div
      className={clsx(
        'surface h-full rounded-lg p-4 text-start transition-colors duration-fast',
        to && 'hover:border-primary/50',
        tone === 'warning' && 'border-warning/40',
        tone === 'error' && 'border-error/40',
      )}
    >
      <div className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{label}</div>
      <div className="mt-2 text-metric tabular text-[var(--text-primary)]">{value}</div>
      {delta && <div className="mt-1">{delta}</div>}
      {hint && <div className="mt-1 text-caption text-muted">{hint}</div>}
    </div>
  );
  return to ? (
    <Link to={to} className="block focus-visible:rounded-lg">
      {body}
    </Link>
  ) : (
    body
  );
}

/** A titled card. */
export function Section({ title, subtitle, action, children, className }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <Card className={className}>
      <CardHeader title={title} subtitle={subtitle} action={action} />
      {children}
    </Card>
  );
}

/** Finance's notices about a figure or a report — each in words when the dictionary has them. */
export function Notices({ notices }: { notices: Notice[] | null | undefined }) {
  const { t, has } = useI18n();
  if (!notices?.length) return null;
  return (
    <ul className="space-y-1">
      {notices.map((n) => {
        const key = `notice.${n.code}`;
        return (
          <li key={n.code} className="flex items-start gap-2 text-caption text-muted">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-fg dark:text-warning-dark" aria-hidden />
            <span>{has(key) ? t(key) : n.code}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Why there is no AI explanation, in words. */
export function aiStateKey(status: string): TranslationKey {
  switch (status) {
    case 'DISABLED':
      return 'state.aiDisabled';
    case 'NOT_CONFIGURED':
      return 'state.aiNotConfigured';
    case 'BUDGET_EXCEEDED':
      return 'state.aiBudget';
    case 'PENDING':
    case 'RUNNING':
      return 'state.aiPending';
    case 'FAILED':
      return 'state.aiFailed';
    default:
      return 'state.aiNotRequested';
  }
}

/**
 * An AI explanation, in the reader's language, with the ids each claim cites — or why there is none. Always
 * labelled as AI's, and never in the way: the figures around it stand without it.
 */
export function AiPanel({ ai, title, action, evidenceLink }: { ai: AiView | null | undefined; title?: ReactNode; action?: ReactNode; evidenceLink?: (id: string) => string | null }) {
  const { t, locale } = useI18n();
  const section = ai?.narrative ? (locale === 'ar' ? ai.narrative.ar : ai.narrative.en) : null;
  const cite = (ids: string[]) =>
    ids.length ? (
      <span className="ms-1 inline-flex flex-wrap gap-1">
        {ids.slice(0, 4).map((id) => {
          const href = evidenceLink?.(id);
          const chip = <span className="rounded bg-[var(--surface-sunken)] px-1.5 py-0.5 font-mono text-[10px] text-muted">{id}</span>;
          return href ? (
            <Link key={id} to={href}>
              {chip}
            </Link>
          ) : (
            <span key={id}>{chip}</span>
          );
        })}
      </span>
    ) : null;
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden />
            {title ?? t('state.aiLabel')}
          </span>
        }
        subtitle={section ? t('state.aiLabel') : undefined}
        action={action}
      />
      {!section ? (
        <p className="flex items-center gap-2 text-body-sm text-muted">
          <Bot className="h-4 w-4" aria-hidden />
          {t('state.aiUnavailable')} — {t(aiStateKey(ai?.status ?? 'NOT_REQUESTED'))}
        </p>
      ) : (
        <div className="space-y-4 text-body-sm">
          <p className="text-[var(--text-primary)]">{section.summary}</p>
          {section.positives.length > 0 && (
            <ul className="space-y-1.5">
              {section.positives.map((p, i) => (
                <li key={i} className="flex gap-2">
                  <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-success-fg dark:text-success flip-rtl" aria-hidden />
                  <span>
                    {p.text}
                    {cite(p.evidenceIds)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {section.risks.length > 0 && (
            <ul className="space-y-1.5">
              {section.risks.map((r, i) => (
                <li key={i} className="flex gap-2">
                  <AlertTriangle className={clsx('mt-0.5 h-4 w-4 shrink-0', r.severity === 'HIGH' ? 'text-error-fg dark:text-error-dark' : 'text-warning-fg dark:text-warning-dark')} aria-hidden />
                  <span>
                    {r.text}
                    {cite(r.evidenceIds)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {section.recommendations.length > 0 && (
            <ul className="space-y-1.5">
              {section.recommendations.map((r, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-0.5 text-primary" aria-hidden>
                    →
                  </span>
                  <span>
                    {r.text}
                    {cite(r.evidenceIds)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {section.limitations.length > 0 && (
            <ul className="space-y-1 border-t hairline pt-3 text-caption text-muted">
              {section.limitations.map((l, i) => (
                <li key={i}>{l}</li>
              ))}
            </ul>
          )}
          {ai?.generatedAt && <p className="text-caption text-[var(--text-tertiary)]">{formatDateTime(ai.generatedAt, locale)} · {ai.model}</p>}
        </div>
      )}
    </Card>
  );
}
